import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';

const REFRESH_INTERVAL = 10000;
const MAX_ENTRIES = 100;
const controlsByConversation = new Map();

const unavailableStatus = () => ({
  available: false,
  paused: null,
  version: null,
  channel: null,
  lifecycle: null,
  mode: null,
  can_pause: false,
  can_resume: false,
  can_takeover: false,
  projection_pending: false,
  sync_required: false,
});

const isVersion = value => typeof value === 'number' && Number.isFinite(value);

export const isActiveWebBia = status =>
  status?.available === true &&
  status.channel === 'web' &&
  status.paused === false &&
  status.lifecycle !== 'human' &&
  status.lifecycle !== 'closed' &&
  (status.can_pause || status.can_resume || status.can_takeover) &&
  (status.lifecycle === 'bia' ||
    status.mode === 'bia' ||
    status.mode === 'active');

export const isAuthoritativeWebBiaActive = status =>
  status?.available === true &&
  status.channel === 'web' &&
  status.paused === false &&
  status.lifecycle !== 'human' &&
  status.lifecycle !== 'closed' &&
  status.lifecycle !== 'none' &&
  (status.lifecycle === 'bia' ||
    status.mode === 'bia' ||
    status.mode === 'active');

export const isKnownWebConversation = conversation =>
  conversation?.custom_attributes?.bia_channel === 'web';

export const isBiaPublicReplyBlocked = ({
  enabled,
  status,
  knownWeb,
  isLoading = false,
}) => {
  if (!enabled || !knownWeb) return false;
  if (isLoading) return true;
  if (status?.available !== true) return true;
  // A browser routing hint must never override an authoritative channel
  // mismatch. Keep public sending fail-closed until the conversation is
  // explicitly reconciled by the server.
  if (status.channel !== 'web') return true;
  if (
    status.lifecycle === 'human' ||
    status.lifecycle === 'closed' ||
    status.mode === 'human' ||
    status.mode === 'closed'
  ) {
    return false;
  }
  if (status.lifecycle === 'handoff_pending' || status.lifecycle === 'none') {
    return false;
  }
  if (status.lifecycle === 'bia' && status.paused === true) return false;
  return true;
};

const normalizeStatus = value => {
  const status = value || {};
  return {
    available: status.available === true,
    paused:
      status.available === true && typeof status.paused === 'boolean'
        ? status.paused
        : null,
    version: isVersion(status.version) ? status.version : null,
    channel: typeof status.channel === 'string' ? status.channel : null,
    lifecycle: typeof status.lifecycle === 'string' ? status.lifecycle : null,
    mode: typeof status.mode === 'string' ? status.mode : null,
    can_pause: status.can_pause === true,
    can_resume: status.can_resume === true,
    can_takeover: status.can_takeover === true,
    projection_pending: status.projection_pending === true,
    sync_required: status.sync_required === true,
  };
};

const isEnabled = () => window.globalConfig?.BIA_CONTROL_ENABLED === true;

const getEntry = key => {
  if (!controlsByConversation.has(key)) {
    controlsByConversation.set(key, {
      status: ref(unavailableStatus()),
      isLoading: ref(false),
      isActing: ref(false),
      error: ref(null),
      subscribers: 0,
      timer: null,
      hasReceivedStatus: false,
      requestSequence: 0,
      appliedSequence: 0,
      latestVersion: null,
      visible: true,
      visibilityHandler: null,
      lastUsed: Date.now(),
    });
  }
  return controlsByConversation.get(key);
};

const pruneEntries = () => {
  if (controlsByConversation.size <= MAX_ENTRIES) return;
  [...controlsByConversation.entries()]
    .filter(([, entry]) => entry.subscribers === 0)
    .sort(([, left], [, right]) => left.lastUsed - right.lastUsed)
    .slice(0, controlsByConversation.size - MAX_ENTRIES)
    .forEach(([key]) => controlsByConversation.delete(key));
};

const clearTimer = entry => {
  if (entry.timer) {
    clearTimeout(entry.timer);
    entry.timer = null;
  }
};

const scheduleRefresh = (entry, fetchStatus) => {
  clearTimer(entry);
  if (entry.subscribers === 0 || !entry.visible) return;
  entry.timer = setTimeout(async () => {
    await fetchStatus();
    scheduleRefresh(entry, fetchStatus);
  }, REFRESH_INTERVAL);
};

const applyStatus = (entry, payload, sequence) => {
  if (sequence < entry.appliedSequence) return false;

  const nextStatus = normalizeStatus(payload);
  if (
    isVersion(entry.latestVersion) &&
    isVersion(nextStatus.version) &&
    nextStatus.version < entry.latestVersion
  ) {
    return false;
  }

  entry.appliedSequence = sequence;
  if (isVersion(nextStatus.version)) entry.latestVersion = nextStatus.version;
  entry.status.value = nextStatus;
  entry.error.value = null;
  return true;
};

const createRequest = (entry, method, url, body) => {
  entry.requestSequence += 1;
  const sequence = entry.requestSequence;
  const request = { sequence, method, url, body };
  return {
    ...request,
    execute: async () => {
      try {
        const response =
          method === 'get'
            ? await window.axios.get(url)
            : await window.axios.post(url, body);
        return { sequence, data: response?.data || {} };
      } catch (error) {
        return { sequence, error };
      }
    },
  };
};

const useEntry = (entry, accountId, conversationId) => {
  const baseUrl = () =>
    `/api/v1/accounts/${accountId}/conversations/${conversationId}/bia_control`;

  const fetchStatus = async () => {
    if (!isEnabled() || !accountId || !conversationId) return null;
    const request = createRequest(entry, 'get', baseUrl());
    // Preserve the last authoritative state while a periodic refresh is in
    // flight. The initial read remains visibly loading, while mutations use
    // isActing for their own feedback.
    entry.isLoading.value = !entry.hasReceivedStatus;
    const result = await request.execute();
    if (result.error) {
      // A failed status request is fail-closed. Labels and cached attributes
      // are intentionally never used as a fallback authority.
      if (result.sequence >= entry.appliedSequence) {
        entry.appliedSequence = result.sequence;
        entry.status.value = unavailableStatus();
        entry.error.value = result.error;
      }
    } else if (applyStatus(entry, result.data, result.sequence)) {
      entry.hasReceivedStatus = true;
    }
    if (result.sequence >= entry.appliedSequence) entry.isLoading.value = false;
    scheduleRefresh(entry, fetchStatus);
    return result;
  };

  const act = async (action, confirmVersion) => {
    if (
      !isEnabled() ||
      !entry.status.value.available ||
      !isVersion(confirmVersion)
    ) {
      return false;
    }
    const request = createRequest(entry, 'post', `${baseUrl()}/${action}`, {
      expected_version: confirmVersion,
    });
    entry.isActing.value = true;
    entry.error.value = null;
    const result = await request.execute();
    if (result.sequence < entry.appliedSequence) {
      entry.isActing.value = false;
      scheduleRefresh(entry, fetchStatus);
      return false;
    }
    if (!result.error) {
      applyStatus(entry, result.data, result.sequence);
      entry.isActing.value = false;
      scheduleRefresh(entry, fetchStatus);
      return true;
    }

    const response = result.error?.response;
    if (response?.status === 409) {
      // A conflict response is never a mutation retry. Refetch authoritative state.
      await fetchStatus();
    } else {
      entry.status.value = unavailableStatus();
      entry.error.value = result.error;
    }
    entry.isActing.value = false;
    scheduleRefresh(entry, fetchStatus);
    return false;
  };

  const onVisibilityChange = () => {
    entry.visible = document.visibilityState !== 'hidden';
    if (!entry.visible) {
      clearTimer(entry);
    } else if (entry.subscribers > 0 && isEnabled()) {
      fetchStatus();
    }
  };

  const attach = () => {
    entry.lastUsed = Date.now();
    if (entry.subscribers === 0) {
      entry.visibilityHandler = onVisibilityChange;
      document.addEventListener('visibilitychange', entry.visibilityHandler);
    }
    entry.subscribers += 1;
    if (entry.subscribers === 1 && isEnabled()) fetchStatus();
  };

  const detach = () => {
    entry.subscribers = Math.max(0, entry.subscribers - 1);
    if (entry.subscribers === 0) {
      clearTimer(entry);
      if (entry.visibilityHandler) {
        document.removeEventListener(
          'visibilitychange',
          entry.visibilityHandler
        );
        entry.visibilityHandler = null;
      }
      entry.lastUsed = Date.now();
      pruneEntries();
    }
  };

  return { fetchStatus, act, attach, detach, onVisibilityChange };
};

export function useBiaControl({ accountId, conversationId }) {
  const enabled = computed(isEnabled);
  const state = shallowRef(null);
  const key = computed(() => {
    if (!enabled.value || !accountId.value || !conversationId.value)
      return null;
    return `${accountId.value}:${conversationId.value}`;
  });
  let attached = null;
  let entryApi = null;

  const connect = nextKey => {
    if (attached) {
      attached.entryApi.detach();
      attached = null;
    }
    if (!nextKey) {
      state.value = null;
      return;
    }
    const entry = getEntry(nextKey);
    entryApi = useEntry(entry, accountId.value, conversationId.value);
    entryApi.attach();
    attached = { entry, entryApi };
    state.value = entry;
  };

  watch(key, connect, { immediate: true });
  onScopeDispose(() => {
    if (attached) {
      attached.entryApi.detach();
      attached = null;
    }
  });

  const status = computed(
    () => state.value?.status.value || unavailableStatus()
  );
  const isLoading = computed(() => state.value?.isLoading.value === true);
  const isActing = computed(() => state.value?.isActing.value === true);
  const hasError = computed(() => !!state.value?.error.value);

  return {
    enabled,
    status,
    isLoading,
    isActing,
    hasError,
    fetchStatus: () => entryApi?.fetchStatus(),
    pause: () => entryApi?.act('pause', status.value.version),
    resume: () => entryApi?.act('resume', status.value.version),
    takeover: () => entryApi?.act('takeover', status.value.version),
  };
}

export const biaControlRegistry = controlsByConversation;
